/**
 * React Hooks for Network Status
 *
 * Provides hooks for monitoring network connectivity in React components
 *
 * @module lib/hooks/use-network-status
 */

'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  networkStatusService,
  type NetworkInfo,
  type NetworkStatus,
  type NetworkType,
} from '../services/network-status';

// ============================================================================
// useNetworkStatus - Main Hook
// ============================================================================

/**
 * Hook to monitor network status with automatic updates
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const network = useNetworkStatus();
 *
 *   if (network.status === 'offline') {
 *     return <OfflineBanner />;
 *   }
 *
 *   return <div>Network: {network.type}</div>;
 * }
 * ```
 */
export function useNetworkStatus() {
  const [networkInfo, setNetworkInfo] = useState<NetworkInfo>(
    networkStatusService.getStatus()
  );
  const listenerIdRef = useRef<string>();

  useEffect(() => {
    // Generate unique listener ID
    listenerIdRef.current = `hook-${Date.now()}-${Math.random()}`;

    // Subscribe to network status changes
    const handleNetworkChange = (info: NetworkInfo) => {
      setNetworkInfo(info);
    };

    networkStatusService.subscribe(listenerIdRef.current, handleNetworkChange);

    // Get current status on mount
    setNetworkInfo(networkStatusService.getStatus());

    // Cleanup
    return () => {
      if (listenerIdRef.current) {
        networkStatusService.unsubscribe(listenerIdRef.current);
      }
    };
  }, []);

  const checkNow = useCallback(async () => {
    const info = await networkStatusService.checkNow();
    setNetworkInfo(info);
    return info;
  }, []);

  return {
    ...networkInfo,
    checkNow,
  };
}

// ============================================================================
// useOnlineStatus - Simple Online/Offline Hook
// ============================================================================

/**
 * Simple hook that returns true/false for online status
 *
 * @example
 * ```tsx
 * function SaveButton() {
 *   const isOnline = useOnlineStatus();
 *
 *   return (
 *     <button disabled={!isOnline}>
 *       {isOnline ? 'Save' : 'Offline - Save Locally'}
 *     </button>
 *   );
 * }
 * ```
 */
export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState(networkStatusService.isOnline());
  const listenerIdRef = useRef<string>();

  useEffect(() => {
    listenerIdRef.current = `online-hook-${Date.now()}-${Math.random()}`;

    const handleNetworkChange = (info: NetworkInfo) => {
      setIsOnline(info.isOnline);
    };

    networkStatusService.subscribe(listenerIdRef.current, handleNetworkChange);
    setIsOnline(networkStatusService.isOnline());

    return () => {
      if (listenerIdRef.current) {
        networkStatusService.unsubscribe(listenerIdRef.current);
      }
    };
  }, []);

  return isOnline;
}

// ============================================================================
// useConnectivityRestored - Callback on Reconnection
// ============================================================================

/**
 * Hook to execute callback when connectivity is restored
 *
 * @example
 * ```tsx
 * function SyncManager() {
 *   useConnectivityRestored(() => {
 *     console.log('Back online! Starting sync...');
 *     syncQueue.processAll();
 *   });
 *
 *   return <SyncStatus />;
 * }
 * ```
 */
export function useConnectivityRestored(callback: () => void) {
  const callbackRef = useRef(callback);

  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const listener = () => {
      callbackRef.current();
    };

    networkStatusService.onConnectivityRestored(listener);

    return () => {
      networkStatusService.offConnectivityRestored(listener);
    };
  }, []);
}

// ============================================================================
// useNetworkType - Get Network Type
// ============================================================================

/**
 * Hook to get current network type (4g, 3g, wifi, etc.)
 *
 * @example
 * ```tsx
 * function QualitySelector() {
 *   const networkType = useNetworkType();
 *
 *   // Reduce photo quality on slower connections
 *   const quality = networkType === '2g' || networkType === '3g' ? 'low' : 'high';
 *
 *   return <PhotoCapture quality={quality} />;
 * }
 * ```
 */
export function useNetworkType(): NetworkType {
  const [networkType, setNetworkType] = useState<NetworkType>(
    networkStatusService.getNetworkType()
  );
  const listenerIdRef = useRef<string>();

  useEffect(() => {
    listenerIdRef.current = `type-hook-${Date.now()}-${Math.random()}`;

    const handleNetworkChange = (info: NetworkInfo) => {
      setNetworkType(info.type);
    };

    networkStatusService.subscribe(listenerIdRef.current, handleNetworkChange);
    setNetworkType(networkStatusService.getNetworkType());

    return () => {
      if (listenerIdRef.current) {
        networkStatusService.unsubscribe(listenerIdRef.current);
      }
    };
  }, []);

  return networkType;
}

// ============================================================================
// useConnectionQuality - Get Connection Quality Metrics
// ============================================================================

/**
 * Hook to get connection quality metrics
 *
 * @example
 * ```tsx
 * function ConnectionQuality() {
 *   const { downlink, rtt, effectiveType } = useConnectionQuality();
 *
 *   return (
 *     <div>
 *       <p>Speed: {downlink} Mbps</p>
 *       <p>Latency: {rtt} ms</p>
 *       <p>Type: {effectiveType}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useConnectionQuality() {
  const [quality, setQuality] = useState({
    downlink: networkStatusService.getDownlinkSpeed(),
    rtt: networkStatusService.getRTT(),
    effectiveType: networkStatusService.getStatus().effectiveType,
    saveData: networkStatusService.isDataSaverEnabled(),
  });
  const listenerIdRef = useRef<string>();

  useEffect(() => {
    listenerIdRef.current = `quality-hook-${Date.now()}-${Math.random()}`;

    const handleNetworkChange = (info: NetworkInfo) => {
      setQuality({
        downlink: info.downlink,
        rtt: info.rtt,
        effectiveType: info.effectiveType,
        saveData: info.saveData || false,
      });
    };

    networkStatusService.subscribe(listenerIdRef.current, handleNetworkChange);

    const currentInfo = networkStatusService.getStatus();
    setQuality({
      downlink: currentInfo.downlink,
      rtt: currentInfo.rtt,
      effectiveType: currentInfo.effectiveType,
      saveData: currentInfo.saveData || false,
    });

    return () => {
      if (listenerIdRef.current) {
        networkStatusService.unsubscribe(listenerIdRef.current);
      }
    };
  }, []);

  return quality;
}

// ============================================================================
// useNetworkStatusEffect - Run Effect on Network Status Change
// ============================================================================

/**
 * Hook to run side effects when network status changes
 *
 * @example
 * ```tsx
 * function DataSync() {
 *   useNetworkStatusEffect((status) => {
 *     if (status === 'online') {
 *       syncData();
 *     }
 *   }, ['online']);
 *
 *   return <SyncIndicator />;
 * }
 * ```
 */
export function useNetworkStatusEffect(
  effect: (status: NetworkStatus) => void,
  triggerStatuses?: NetworkStatus[]
) {
  const effectRef = useRef(effect);
  const listenerIdRef = useRef<string>();

  // Keep effect ref up to date
  useEffect(() => {
    effectRef.current = effect;
  }, [effect]);

  useEffect(() => {
    listenerIdRef.current = `effect-hook-${Date.now()}-${Math.random()}`;

    const handleNetworkChange = (info: NetworkInfo) => {
      // If specific statuses provided, only trigger for those
      if (triggerStatuses && !triggerStatuses.includes(info.status)) {
        return;
      }

      effectRef.current(info.status);
    };

    networkStatusService.subscribe(listenerIdRef.current, handleNetworkChange);

    return () => {
      if (listenerIdRef.current) {
        networkStatusService.unsubscribe(listenerIdRef.current);
      }
    };
  }, [triggerStatuses]);
}

// ============================================================================
// useAutoSync - Automatic Sync on Connectivity Restore
// ============================================================================

/**
 * Hook to automatically trigger sync when connectivity is restored
 *
 * @example
 * ```tsx
 * function App() {
 *   useAutoSync(async () => {
 *     await syncQueue.processAll();
 *     await syncJobs();
 *     await syncInspections();
 *   });
 *
 *   return <AppContent />;
 * }
 * ```
 */
export function useAutoSync(syncFunction: () => void | Promise<void>) {
  const syncRef = useRef(syncFunction);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [syncError, setSyncError] = useState<Error | null>(null);

  // Keep sync function ref up to date
  useEffect(() => {
    syncRef.current = syncFunction;
  }, [syncFunction]);

  useConnectivityRestored(() => {
    const performSync = async () => {
      try {
        setIsSyncing(true);
        setSyncError(null);
        await syncRef.current();
        setLastSyncTime(new Date());
      } catch (error) {
        setSyncError(error as Error);
        console.error('[useAutoSync] Sync failed:', error);
      } finally {
        setIsSyncing(false);
      }
    };

    performSync();
  });

  return {
    isSyncing,
    lastSyncTime,
    syncError,
  };
}
